import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { FormGroup, AbstractControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { ToastService } from "@tylertech/core-module";
import { IUnit } from "@tylertech/tyler-api-types";
import { InventoryUnitService } from "../../services";
import { HttpErrorResponse } from "@angular/common/http";
export interface NewUnitDialogData
{
    readonly description: string;
}
@Component({
    selector: "new-unit-dialog-component",
    templateUrl: "./addNewUnitDialog.tmpl.html",
    styleUrls: ["./addNewUnitDialog.component.scss"],
    standalone: false
})
export class NewUnitDialogComponent implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<NewUnitDialogComponent, IUnit | undefined>>(MatDialogRef);
    private readonly data = inject<NewUnitDialogData>(MAT_DIALOG_DATA);
    private readonly $toastService = inject(ToastService);
    private readonly $inventoryUnitService = inject(InventoryUnitService);

    private readonly destructor$ = new Subject<void>();
    readonly formGroup: FormGroup;
    readonly commands: Array<Command>;
    private newUnit: IUnit;
    saving: boolean;
    constructor()
    {
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.cancel(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Save & Close",
                action: () => this.save(),
                getEnabled: () => this.formGroup.valid,
            }),
        ];
        this.formGroup = new FormGroup({
            code: LgdFormControlFactory.text(undefined, true, { maxLength: 20 }),
            description: LgdFormControlFactory.text(undefined, true, { maxLength: 50 }),
            conversionFactor: LgdFormControlFactory.number(undefined, true, {
                min: 1e-8,
                max: 1,
                scale: 8,
            }),
        });
    }

    private get description(): string
    {
        return this.data.description;
    }

    private get codeControl(): AbstractControl
    {
        return this.formGroup.get("code");
    }

    private get descriptionControl(): AbstractControl
    {
        return this.formGroup.get("description");
    }

    private get conversionFactorControl(): AbstractControl
    {
        return this.formGroup.get("conversionFactor");
    }

    get Description(): string
    {
        return this.newUnit.Description;
    }

    set Description(value: string)
    {
        this.newUnit.Description = value;
    }

    get Code(): string
    {
        return this.newUnit.Code;
    }

    set Code(value: string)
    {
        this.newUnit.Code = value;
    }

    get ConversionFactor(): number
    {
        return this.newUnit.ConversionFactor;
    }

    set ConversionFactor(value: number)
    {
        this.newUnit.ConversionFactor = value;
    }

    get MinimumNumber(): number
    {
        return 1e-8;
    }

    get ValidConversionFactor(): boolean
    {
        return this.ConversionFactor && 0 < this.ConversionFactor && this.ConversionFactor <= 1;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.newUnit = this.data.description
            ? {
                Id: undefined,
                Code: this.description.length > 20 ? this.data.description.substring(0, 20) : this.data.description,
                Description:
                    this.data.description.length > 50
                        ? this.data.description.substring(0, 50)
                        : this.data.description,
                ConversionFactor: 0,
            }
            : {
                Id: undefined,
                Code: undefined,
                Description: undefined,
                ConversionFactor: 0,
            };
        this.codeControl.setValue(this.newUnit.Code);
        this.descriptionControl.setValue(this.newUnit.Description);
        this.conversionFactorControl.setValue(this.newUnit.ConversionFactor);
        subscribeToValueChanges(this.codeControl, (newValue: string) =>
        {
            this.newUnit.Code = newValue;
        });
        subscribeToValueChanges(this.descriptionControl, (newValue: string) =>
        {
            this.newUnit.Description = newValue;
        });
        subscribeToValueChanges(this.conversionFactorControl, (newValue: number) =>
        {
            this.newUnit.ConversionFactor = newValue;
        });
        function subscribeToValueChanges<T>(control: AbstractControl, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    cancel(): void
    {
        this.dialogRef.close();
    }

    getConversionString(): string
    {
        if (this.ValidConversionFactor)
        {
            const units = 1 / this.ConversionFactor;
            return `1 Item = ${units} Unit(s)`;
        }
        return "";
    }

    save(): void
    {
        this.saving = true;
        this.$inventoryUnitService
            .AddUnit(this.newUnit)
            .then(
                (result): void =>
                {
                    this.$toastService.showToast("New Unit was added successfully");
                    this.dialogRef.close(result);
                },
                (result: HttpErrorResponse): void =>
                {
                    this.$toastService.showToast(result.error.Message);
                }
            )
            .finally(() =>
            {
                this.saving = false;
            });
    }
}
